import React, { useEffect, useState } from "react";

import {
  Box,
  Button,
  Divider,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { GoogleLogin } from "react-google-login";

import { GreyDark } from "assets/colors";
import InputSelectLanguage from "components/Input/InputSelect/InputSelectLanguage";
import InputText, {
  INPUT_TEXT_TYPE,
} from "components/Input/InputText/InputText";
import { useActionsLegacy } from "hooks/useActions";
import useTranslations from "hooks/useTranslations";

import fieldProLogo from "../../assets/images/svg/fieldProLogo.svg";
import Progress from "../../components/Progress/CustomCircularProgress";
import { stopRetryWaitAction } from "./redux/actionCreator";
import styles from "./styles";

interface ILoginFormProps {
  onFormSubmit: (email: string, password: string) => void;
  onGoogleLogin: (googleData: any) => void;
  selectedLanguage: string;
  retryWait: boolean;
  handleLangChange: (lang: string) => void;
  isLoggingIn: boolean;
  displayError: boolean;
  errorMessage?: string;
  onOpenClosePasswordLost: () => void;
}

/**
 * LoginForm component for the login form
 */

const LoginForm: React.FC<ILoginFormProps> = ({
  onFormSubmit,
  onGoogleLogin,
  selectedLanguage,
  retryWait,
  handleLangChange,
  isLoggingIn,
  displayError,
  errorMessage,
  onOpenClosePasswordLost,
}) => {
  const [countdownSec, setCountdownSec] = useState<number>(retryWait ? 30 : 0);
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const useStyle = makeStyles(styles as any);
  const classes = useStyle();
  const lang = useTranslations();
  const stopRetryWait = useActionsLegacy(stopRetryWaitAction);

  const handleInputChange = (value: any, name: string) => {
    switch (name) {
      case "email":
        setEmail(value);
        break;
      case "password":
        setPassword(value);
        break;
    }
  };

  /**
   * Handles click on login
   */
  const handleConfirmAction = () => {
    onFormSubmit(email, password);
  };

  const handleGoogleLoginAction = (googleData: any) => {
    onGoogleLogin(googleData);
  };

  const keyPress = (event: any) => {
    if (event.key === "Enter") {
      if (confirmButtonEnabled()) {
        handleConfirmAction();
      }
    }
  };

  const confirmButtonEnabled = () => {
    const errors = {
      email: email.length === 0,
      password: password.length === 0,
      retryWait: retryWait === true && countdownSec > 0,
    };
    // @ts-expect-error TS7053
    return Object.keys(errors).some((x) => errors[x]);
  };

  const langLabel = lang.containers.login.subCategories.login;
  const isDisabled = confirmButtonEnabled();
  let errorMessageToDisplay = errorMessage;

  if (countdownSec > 0 && retryWait) {
    errorMessageToDisplay = `${errorMessage} Try again in ${countdownSec}s`;
  }

  useEffect(() => {
    const interval = setInterval(() => {
      if (countdownSec === 0) {
        setCountdownSec(30);
        stopRetryWait();
      } else {
        setCountdownSec((countdownSec) => countdownSec - 1);
      }
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={classes.FormContainer}>
      <img
        src={fieldProLogo}
        className={classes.fieldProLoginLogo}
        alt="fieldProLogo"
        style={{ position: "absolute", top: "24px", left: "32px" }}
      />

      <Box
        style={{
          position: "absolute",
          top: "24px",
          right: "40px",
        }}
      >
        <InputSelectLanguage
          options={[
            { key: "en", label: "gb", value: "English" },
            { key: "fr", label: "fr", value: "Français" },
            { key: "es", label: "es", value: "Español" },
          ]}
          value={selectedLanguage}
          onChange={handleLangChange}
        />
      </Box>

      <Box marginBottom="100px" />

      <Typography
        style={{ fontSize: "37px" }}
        variant="h6"
        className={classes.TitleLogin}
      >
        {langLabel.titleLogin}
      </Typography>
      <Typography
        style={{ fontSize: "16px" }}
        variant="subtitle1"
        className={classes.SubTitleLogin}
      >
        {langLabel.subTitleLogin1}
      </Typography>
      <Typography
        style={{ fontSize: "24px", margin: "20px 0" }}
        variant="subtitle2"
        className={classes.SubTitle2Login}
      >
        {langLabel.subTitleLogin2}
      </Typography>

      <Box marginBottom="-10px">
        <InputText
          label={langLabel.inputEmail.title}
          placeholder={"yourworkemail@yourcompany.com"}
          darkMode={false}
          onChange={handleInputChange}
          type={INPUT_TEXT_TYPE.EMAIL}
          name="email"
          autoComplete="off"
          onKeyPress={keyPress}
          lowercase
        />
      </Box>

      <InputText
        label={langLabel.inputPassword.title}
        name="password"
        darkMode={false}
        type={INPUT_TEXT_TYPE.PASSWORD}
        onChange={handleInputChange}
        autoComplete="off"
        onKeyPress={keyPress}
        showPasswordButton={true}
        placeholder={"••••••••••••••"}
        error={displayError && !isLoggingIn ? errorMessageToDisplay : ""}
      />

      <div className={classes.LoginFormPasswordForgotten}>
        <span
          style={{ cursor: "pointer", float: "right" }}
          onClick={onOpenClosePasswordLost}
          role="button"
        >
          {langLabel.passwordLostButton}
        </span>
      </div>

      <div className={classes.LoginButtonContainer}>
        {isLoggingIn ? (
          <div className={classes.ProgressContainer}>
            <Progress darkMode={true} />
          </div>
        ) : (
          <React.Fragment>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleConfirmAction}
              aria-label={"Login"}
              aria-hidden="true"
              disabled={isDisabled}
              disableElevation
            >
              {langLabel.emailPasswordLoginButton}
            </Button>

            <Grid container alignItems="center" spacing={3}>
              <Grid item xs>
                <Divider />
              </Grid>
              <Grid item>
                <Box
                  fontWeight={700}
                  fontSize={"20px"}
                  lineHeight={"24px"}
                  style={{ color: GreyDark }}
                >
                  {langLabel.orSeparator}
                </Box>
              </Grid>
              <Grid item xs>
                <Divider />
              </Grid>
            </Grid>

            <GoogleLogin
              clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID ?? ""}
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "0px",
                fontWeight: "bold",
              }}
              buttonText={langLabel.googleLoginButton}
              className={classes.GoogleButton}
              onSuccess={handleGoogleLoginAction}
              onFailure={handleGoogleLoginAction}
              cookiePolicy={"single_host_origin"}
            />
            <Box display={"inline-block"} paddingTop={"16px"}>
              {langLabel.trial.customMessage.dontHaveAnAccount}
              <a
                href="https://www.fieldproapp.com/book-a-demo"
                className={classes.bookDemoLink}
                target="_blank"
                rel="noreferrer"
              >
                {" "}
                {langLabel.demo.customMessage.bookADemo}
              </a>
            </Box>
            {/* <Box display={"inline-block"} paddingTop={"16px"}>
              {langLabel.trial.customMessage.dontHaveAnAccount}
              <Link
                to={SIGNUP_ROUTE}
                style={{ color: Green, fontWeight: "700" }}
              >
                {" "}
                {langLabel.trial.customMessage.signUpForFreeTrial}
              </Link>
            </Box> */}
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

export default LoginForm;
